import { useMemo, useState } from "react";
import EventAnalytics from "./EventAnalytics";
import { generateMockChartSeries, generateMockEventList } from "./mockData";

const PRESET_TO_RANGE = (preset) => {
  const to = new Date();
  const from = new Date();
  switch (preset) {
    case "today":
      from.setHours(0, 0, 0, 0);
      break;
    case "24h":
      from.setTime(to.getTime() - 24 * 36e5);
      break;
    case "7d":
      from.setDate(to.getDate() - 7);
      break;
    case "90d":
      from.setDate(to.getDate() - 90);
      break;
    case "30d":
    default:
      from.setDate(to.getDate() - 30);
  }
  return { from: from.toISOString(), to: to.toISOString() };
};

const resolutionForPreset = (preset) => {
  if (preset === "today" || preset === "24h") return "hourly";
  if (preset === "90d") return "weekly";
  return "daily";
};

const PAGE_SIZE = 6;

/**
 * Self-contained demo — no Redux, no API. Useful for Storybook / local dev / design review.
 *
 * <DemoEventAnalytics />
 * <DemoEventAnalytics scenario="quiet_range" />
 * <DemoEventAnalytics scenario="locked_resolution" isPremium={false} />
 */
const DemoEventAnalytics = ({ scenario = "healthy", isPremium = true, showDemoBadge = true }) => {
  const [preset, setPreset] = useState(scenario === "locked_resolution" ? "today" : "30d");
  const [selectedEventUuid, setSelectedEventUuid] = useState(null);
  const [sort, setSort] = useState({ field: "impressions", direction: "desc" });
  const [page, setPage] = useState(1);

  const dateRange = useMemo(() => ({ preset, ...PRESET_TO_RANGE(preset) }), [preset]);
  const resolution = resolutionForPreset(preset);
  const resolutionLocked = resolution === "hourly" && !isPremium;
  const effectiveResolution = resolutionLocked ? "daily" : resolution;

  const allEvents = useMemo(
    () => (scenario === "quiet_range" ? [] : generateMockEventList(dateRange, effectiveResolution, 18)),
    [scenario, dateRange, effectiveResolution]
  );

  const sortedEvents = useMemo(() => {
    const copy = [...allEvents];
    copy.sort((a, b) => {
      const dir = sort.direction === "asc" ? 1 : -1;
      if (sort.field === "name") return a.name.localeCompare(b.name) * dir;
      return ((a[sort.field] ?? 0) - (b[sort.field] ?? 0)) * dir;
    });
    return copy;
  }, [allEvents, sort]);

  const pageItems = sortedEvents.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  const totalPages = Math.max(1, Math.ceil(sortedEvents.length / PAGE_SIZE));

  const chartSeries = useMemo(
    () => (scenario === "quiet_range" ? [] : generateMockChartSeries(dateRange, effectiveResolution)),
    [scenario, dateRange, effectiveResolution]
  );

  const selectedEvent = allEvents.find((e) => e.event_uuid === selectedEventUuid) || null;

  return (
    <div style={{ position: "relative" }}>
      {showDemoBadge && (
        <span className="badge badge-secondary" style={{ position: "absolute", top: -28, right: 0 }}>
          Demo data
        </span>
      )}
      <EventAnalytics
        dateRange={dateRange}
        onDateRangeChange={(next) => {
          setPreset(next.preset);
          setPage(1);
          setSelectedEventUuid(null);
        }}
        resolution={effectiveResolution}
        resolutionLocked={resolutionLocked}
        chartSeries={chartSeries}
        selectedEvent={selectedEvent}
        onSelectEvent={setSelectedEventUuid}
        events={{
          items: pageItems,
          pagination: { page, limit: PAGE_SIZE, total: sortedEvents.length, total_pages: totalPages },
        }}
        sort={sort}
        onSortChange={(next) => {
          setSort(next);
          setPage(1);
        }}
        page={page}
        onPageChange={setPage}
        loading={{ chart: false, table: false }}
        error={{ chart: null, table: null }}
      />
    </div>
  );
};

export default DemoEventAnalytics;
